/**
 * Montant en toutes lettres — règle 5.
 *
 * Écrit à la main plutôt qu'avec `n2words` : la composition
 * « … dirhams et … centime(s) » et les accords (cent/cents, quatre-vingt(s),
 * mille invariable, « de » après million) doivent être garantis au centime
 * près sur une pièce comptable. Une seule implémentation, testée, partagée par
 * l'API (qui la stocke dans `montant_lettres`) et par l'aperçu du formulaire.
 */

import { round2 } from './common.ts';

const UNITES = [
  'zéro',
  'un',
  'deux',
  'trois',
  'quatre',
  'cinq',
  'six',
  'sept',
  'huit',
  'neuf',
  'dix',
  'onze',
  'douze',
  'treize',
  'quatorze',
  'quinze',
  'seize',
  'dix-sept',
  'dix-huit',
  'dix-neuf',
] as const;

const DIZAINES = [
  '',
  '',
  'vingt',
  'trente',
  'quarante',
  'cinquante',
  'soixante',
  'soixante',
  'quatre-vingt',
  'quatre-vingt',
] as const;

function mot(n: number): string {
  return UNITES[n] ?? '';
}

/**
 * 0 – 99. `avantMille` supprime le s de « quatre-vingts », qui reste invariable
 * devant mille : « quatre-vingt mille », mais « quatre-vingts millions ».
 */
function deuxChiffres(n: number, avantMille = false): string {
  if (n < 20) return mot(n);

  const d = Math.floor(n / 10);
  const u = n % 10;
  const base = DIZAINES[d] ?? '';

  // 70-79 et 90-99 se construisent sur soixante / quatre-vingt + 10..19.
  if (d === 7 || d === 9) {
    if (d === 7 && u === 1) return 'soixante et onze';
    return `${base}-${mot(10 + u)}`;
  }

  if (u === 0) return d === 8 && !avantMille ? 'quatre-vingts' : base;
  // 81 ne prend pas de « et », contrairement à 21, 31 … 71.
  if (u === 1 && d !== 8) return `${base} et un`;
  return `${base}-${mot(u)}`;
}

/**
 * 0 – 999.
 * `avantMille` supprime les accords : « deux cent mille », « quatre-vingt
 * mille » s'écrivent sans s, alors que « deux cents millions » le garde
 * (million est un nom, mille un adjectif numéral invariable).
 */
function troisChiffres(n: number, avantMille = false): string {
  const c = Math.floor(n / 100);
  const r = n % 100;

  if (c === 0) return deuxChiffres(r, avantMille);

  const cent = c === 1 ? 'cent' : `${mot(c)} cent`;
  if (r === 0) return c > 1 && !avantMille ? `${cent}s` : cent;

  return `${cent} ${deuxChiffres(r, avantMille)}`;
}

const ECHELLES = [
  { valeur: 1_000_000_000, singulier: 'milliard', pluriel: 'milliards' },
  { valeur: 1_000_000, singulier: 'million', pluriel: 'millions' },
] as const;

/** Partie entière en toutes lettres. */
export function nombreEnLettres(n: number): string {
  const entier = Math.floor(Math.abs(n));
  if (entier === 0) return 'zéro';

  const parties: string[] = [];
  let reste = entier;

  for (const e of ECHELLES) {
    const q = Math.floor(reste / e.valeur);
    if (q > 0) {
      parties.push(`${troisChiffres(q)} ${q > 1 ? e.pluriel : e.singulier}`);
      reste -= q * e.valeur;
    }
  }

  const milliers = Math.floor(reste / 1000);
  if (milliers > 0) {
    // « mille » est invariable et ne se dit jamais « un mille ».
    parties.push(milliers === 1 ? 'mille' : `${troisChiffres(milliers, true)} mille`);
    reste -= milliers * 1000;
  }

  if (reste > 0) parties.push(troisChiffres(reste));

  return parties.join(' ');
}

/** « de » s'intercale après un million / milliard nu : « un million de dirhams ». */
function exigeDe(lettres: string): boolean {
  return /(million|milliard)s?$/.test(lettres);
}

export type MontantEnLettresOptions = {
  /** Met la première lettre en majuscule. */
  capitaliser?: boolean;
  devise?: { singulier: string; pluriel: string };
  centime?: { singulier: string; pluriel: string };
};

const DEVISE_DEFAUT = { singulier: 'dirham', pluriel: 'dirhams' };
const CENTIME_DEFAUT = { singulier: 'centime', pluriel: 'centimes' };

/**
 * `1500` -> « mille cinq cents dirhams », `1250.5` -> « mille deux cent
 * cinquante dirhams et cinquante centimes ».
 *
 * Un montant rond s'arrête aux dirhams : « et zéro centime » n'apporte rien sur
 * une facture, et c'est ainsi que la mention est rédigée à la main.
 * Les montants négatifs sont préfixés « moins ».
 */
export function montantEnLettres(montant: number, options: MontantEnLettresOptions = {}): string {
  const devise = options.devise ?? DEVISE_DEFAUT;
  const centime = options.centime ?? CENTIME_DEFAUT;

  const valeur = round2(Number.isFinite(montant) ? montant : 0);
  const negatif = valeur < 0;

  // On passe par les centimes entiers pour éviter toute dérive binaire.
  const totalCentimes = Math.round(Math.abs(valeur) * 100);
  const dirhams = Math.floor(totalCentimes / 100);
  const centimes = totalCentimes % 100;

  const lettresDirhams = nombreEnLettres(dirhams);
  const nomDevise = dirhams > 1 ? devise.pluriel : devise.singulier;
  const liaison = exigeDe(lettresDirhams) ? ' de' : '';

  const partieCentimes =
    centimes === 0
      ? ''
      : ` et ${nombreEnLettres(centimes)} ${centimes > 1 ? centime.pluriel : centime.singulier}`;

  const phrase =
    `${negatif ? 'moins ' : ''}${lettresDirhams}${liaison} ${nomDevise}${partieCentimes}`;

  return options.capitaliser ? phrase.charAt(0).toUpperCase() + phrase.slice(1) : phrase;
}
